/**
 * O robô que responde a tabela de preço (F193).
 *
 * O cliente escreve "tela iphone 12 pro" e recebe, em segundos, as telas do
 * iPhone 12 Pro com estoque e preço — como o robô do sistema antigo fazia no
 * WhatsApp público da Elite. Tudo aqui é regra pura: nada de banco, nada de
 * dinheiro (este pacote não depende de `@aeris/money`, por
 * `communication-is-standalone`), nada de IA — decisão do dono.
 *
 * ## O casamento que decide se a resposta presta
 *
 * **"a03" não pode trazer "A03 CORE".** O robô antigo testava os
 * modificadores por substring ("go" casava "google") e juntava todos os
 * tokens em qualquer ordem. Aqui:
 *
 * 1. Nome e pergunta viram **tokens** — sequências de letras e dígitos, sem
 *    acento. `A03.` → `a03`; `4G/5G` → `4g` `5g`; `A03s` continua `a03s`,
 *    um token diferente de `a03`.
 * 2. O modelo casa quando os tokens dele aparecem **contíguos** no nome **e o
 *    token seguinte não é um intruso** (`pro`, `max`, `core`, `plus`…).
 *    `f. A03 CORE COM ARO` tem `a03` seguido de `core`: recusado.
 *    `f. A03. A02s. A03s` tem `a03` seguido de `a02s`: aceito.
 * 3. Basta **uma** ocorrência limpa. `f. Iphone 12. Iphone 12 Pro` serve para
 *    "iphone 12" (a primeira ocorrência é seguida de `iphone`) e para
 *    "iphone 12 pro" (a segunda é seguida de `12`).
 *
 * 4G/5G não é intruso por padrão: "tela a32" traz as duas, e o nome de cada
 * uma diz qual é; "tela a32 5g" traz só a 5G, porque `a32 5g` precisa estar
 * contíguo. Quem quiser outro comportamento edita a lista na tela.
 */
/** A lista do robô antigo, sem `moto` (é nome de modelo aqui: Moto G22). */
export const DEFAULT_STOP_WORDS = [
    'de',
    'da',
    'do',
    'dos',
    'das',
    'para',
    'pra',
    'com',
    'em',
    'por',
    'e',
    'o',
    'a',
    'os',
    'as',
    'no',
    'na',
    'um',
    'uma',
    'oi',
    'ola',
    'opa',
    'boa',
    'bom',
    'tarde',
    'dia',
    'noite',
    'vc',
    'voce',
    'tem',
    'ai',
    'agora',
    'gostaria',
    'saber',
    'se',
    'essa',
    'esse',
    'aquele',
    'acaso',
    'teria',
    'alguma',
    'algum',
    'favor',
    'gentileza',
    'pode',
    'me',
    'ver',
    'arruma',
    'consegue',
    'qual',
    'valor',
    'preco',
    'quanto',
    'custa',
    'sai',
    'fica',
    'ta',
    'esta',
    'queria',
    'quero',
    'preciso',
    'procuro',
    'obrigado',
    'obg',
    'ae',
    'blz',
];
export const DEFAULT_INTRUDERS = [
    'plus',
    'max',
    'pro',
    'ultra',
    'prime',
    'lite',
    'mix',
    'core',
    'mini',
    'power',
    'play',
    'go',
    'zoom',
    'neo',
    'fusion',
    'fe',
];
export const DEFAULT_QUALITY_LABELS = ['maxter pro'];
/**
 * Só marca que NÃO aparece no nome das peças. `iphone`, `redmi`, `poco`,
 * `moto` estão no cadastro ("f. IPHONE X", "f. Redmi Note 12") e não podem
 * ser relaxados — sem eles "tela iphone x" viraria "tela x".
 */
export const DEFAULT_BRAND_TOKENS = [
    'samsung',
    'galaxy',
    'motorola',
    'xiaomi',
    'apple',
    'asus',
    'nokia',
    'positivo',
    'multilaser',
    'realme',
    'tcl',
    'zte',
    'lg',
    'infinix',
];
export const DEFAULT_PRICE_SECTIONS = [
    {
        key: 'telas',
        title: 'Telas',
        emoji: '📱',
        triggers: [
            'tela',
            'telas',
            'frontal',
            'frontais',
            'display',
            'lcd',
            'touch',
        ],
        namePrefix: 'f.',
    },
    {
        key: 'baterias',
        title: 'Baterias',
        emoji: '🔋',
        triggers: ['bateria', 'baterias', 'bat'],
        namePrefix: 'bat.',
    },
];
export const DEFAULT_MAX_ITEMS = 20;
/**
 * Marca que o cadastro escreve às vezes (06/10 e 07/10).
 *
 * - **redmi:** o cliente da Elite mandou "Display Redmi note 14" e o robô
 *   ficou quieto: a tela é "f. NOTE 13 4G. NOTE 14…" — a linha Note nunca leva
 *   "Redmi" no nome. Mas "Redmi 13" leva ("f. Redmi 12… Redmi 13"), e por isso
 *   "redmi" não pode entrar em `brandTokens`. Ela só sai se sobrarem DUAS
 *   palavras: "redmi note 14" → "note 14"; "redmi 13" nunca vira "13", que
 *   traria as telas do Note 13, e "redmi a2" nunca vira o Mi A2.
 * - **moto (F244):** "moto g30" contra "f. G10. G20. G30 COM ARO" e
 *   "f. MOTO G10 . G30". Basta sobrar um código ("g30", "e22").
 *
 * Elas saem depois das outras tentativas. A última tira a marca também do
 * NOME do cadastro: "flex power g24" casa "FLEX POWER MOTO G24".
 */
export const SERIES_BRAND_TOKENS = ['redmi', 'moto'];
/**
 * Como o cliente escreve e o cadastro não (F244): o apelido vira a palavra
 * do cadastro antes de tudo. Saiu do log real de GV.
 */
export const MODEL_ALIASES = {
    ip: 'iphone',
    ifone: 'iphone',
    infone: 'iphone',
    iphne: 'iphone',
    ipone: 'iphone',
    iphome: 'iphone',
    aifone: 'iphone',
    redimi: 'redmi',
    redme: 'redmi',
    infinity: 'infinix',
    not: 'note',
    fuziom: 'fusion',
    fusiom: 'fusion',
    trazeira: 'traseira',
};
/**
 * Qualidade e cor saem da pergunta (F244): o robô lista todas as qualidades
 * do modelo, e o cliente escolhe. "tela a26 sem aro", "J7 prime branca",
 * "bateria iphone 11 hk". Só qualidade, sem modelo, é pergunta vaga.
 */
export const QUALIFIER_WORDS = [
    'aro',
    'nn',
    'sem',
    'incell',
    'oled',
    'wk',
    'master',
    'wefix',
    'maxter',
    'original',
    'calibrada',
    'calibra',
    'calibrar',
    'lcd',
    'hk',
    'rox',
    'foxconn',
    'gold',
    'cor',
    'preta',
    'preto',
    'branca',
    'branco',
    'azul',
    'dourado',
    'dourada',
    'rosa',
    'verde',
    'roxo',
    'cinza',
    'vermelha',
    'vermelho',
    'prata',
    'grafite',
    'amarelo',
    'amarela',
];
/**
 * Outras peças (F244). Quando não chamam uma seção cadastrada, seguram a
 * resposta: "câmera frontal do Poco X6" não é tela, "tampa de bateria" não é
 * bateria, "placa mãe" não é placa de carga. Ligadas por "e" a outra peça
 * ("tela e tampa do note 9"), são outro item e saem.
 */
export const OTHER_PART_WORDS = [
    'tampa',
    'tampas',
    'traseira',
    'lente',
    'lentes',
    'camera',
    'cameras',
    'flex',
    'placa',
    'conector',
    'dock',
    'dok',
    'carga',
    'carcaca',
    'chassi',
    'vidro',
    'oca',
    'cola',
    'pelicula',
    'capa',
    'capinha',
    'auricular',
    'botao',
    'sub',
    'campainha',
    'microfone',
    'alto',
    'falante',
    'mae',
];
/**
 * Conversa de pós-venda (F244): o robô fica quieto e a vendedora responde.
 * "Você enviou a tela do c85?", "a tela do a55 tá dando toque fantasma".
 */
export const AFTER_SALE_WORDS = [
    'mandou',
    'mandei',
    'enviou',
    'enviei',
    'comprei',
    'troca',
    'trocar',
    'defeito',
    'problema',
    'fantasma',
    'risca',
    'riscada',
    'riscado',
    'quebrou',
    'quebrada',
    'errado',
    'errada',
    'devolver',
    'devolucao',
    'garantia',
    'endereco',
    'entregar',
];
/**
 * A linha colada no número (07/10): "iP13 Pro" é o iPhone 13 Pro, "note8" o
 * Note 8, "Redmi12" o Redmi 12. Só estas linhas: "a14" e "g30" são códigos, e
 * "MI11T" é como o cadastro escreve.
 */
const GLUED_LINE = /(^|[^a-z0-9])(ip|iphone|ifone|infone|iphne|ipone|iphome|aifone|note|redmi|redimi|redme)(?=\d{1,2}[a-z]?(?:$|[^a-z0-9]))/g;
/** Letra que o cliente separa do número: "a 14" é o A14, "g 30" o G30. */
const JOIN_LETTERS = new Set(['a', 's', 'j', 'g', 'm', 'k']);
/** Quantidade por extenso: "duas telas a15". */
const NUMBER_WORDS = new Set(['duas', 'dois', 'tres', 'quatro', 'cinco']);
/** Marca ou linha, sem código: "tela do iPhone" não diz qual. */
const GENERIC_WORDS = new Set([
    'iphone',
    'moto',
    'redmi',
    'poco',
    'note',
    'samsung',
    'galaxy',
    'xiaomi',
    'realme',
    'infinix',
    'tablet',
    'tab',
    'mi',
]);
/** A rede não diz o modelo: "a 4g" não diz qual. */
const NETWORK_TOKENS = new Set(['3g', '4g', '5g']);
/** Entre uma peça e outra: "tampa DE bateria", "lente DA câmera". */
const CONNECTORS = new Set(['de', 'da', 'do', 'das', 'dos']);
/** Preço na mensagem: o cliente já está pedindo, ou colou a lista. */
const PRICE_PATTERN = /r\$|\d+,\d{2}\b|\d+\s*\$/;
/** Várias peças numa mensagem: uma por linha, ou separadas por "/". */
const MAX_PARTS = 5;
/**
 * Quem tirou "redmi" da pergunta não quer outra marca: "redmi note 12" não
 * traz a tela do Infinix Note 12. Poco, Mi e Xiaomi são da família Redmi.
 */
const BRAND_FAMILIES = {
    redmi: ['redmi', 'xiaomi', 'poco', 'mi'],
    moto: ['moto', 'motorola'],
};
const BRAND_WORDS = new Set([
    'iphone',
    'samsung',
    'galaxy',
    'infinix',
    'realme',
    'oppo',
    'tecno',
    'lg',
    'nokia',
    'asus',
    'zenfone',
    'huawei',
    'motorola',
    'moto',
    'redmi',
    'xiaomi',
    'poco',
    'mi',
]);
const ALIASES = new Map(Object.entries(MODEL_ALIASES));
const QUALIFIERS = new Set(QUALIFIER_WORDS);
const AFTER_SALE = new Set(AFTER_SALE_WORDS);
const SERIES = new Set(SERIES_BRAND_TOKENS);
/** Minúsculas e sem acento — "Batería" e "bateria" são a mesma palavra. */
export const normalizeText = (text) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
/** Letras e dígitos; qualquer outra coisa separa. */
export const tokenize = (text) => normalizeText(text)
    .split(/[^a-z0-9]+/)
    .filter((token) => token !== '');
/**
 * Os tokens do NOME de uma peça, sem os rótulos de qualidade. O "+" colado
 * vira "plus" (F244): "J6+" é o J6 Plus, e "tela j6" não pode trazê-lo.
 */
export const nameTokens = (name, qualityLabels) => {
    let text = normalizeText(name)
        .replace(/([a-z0-9])\+/g, '$1 plus ')
        .replace(GLUED_LINE, '$1$2 ');
    for (const label of qualityLabels) {
        const phrase = tokenize(label);
        if (phrase.length === 0)
            continue;
        // O rótulo como sequência de tokens, com qualquer separador entre eles.
        const pattern = new RegExp(`(^|[^a-z0-9])${phrase.join('[^a-z0-9]+')}(?=$|[^a-z0-9])`, 'g');
        text = text.replace(pattern, '$1 ');
    }
    return tokenize(inheritBrand(text));
};
/**
 * A lista do cadastro não repete a marca (F367, 30/10): "LENTE DE CAMERA
 * IPHONE 13 PRO. 13 PRO MAX" é o 13 Pro e o 13 PRO MAX, ambos iPhone. A parte
 * que começa só com número herda a palavra que vinha antes do número na parte
 * anterior — "13 pro max" vira "iphone 13 pro max", e a pergunta "iphone 13
 * pro max" casa contígua. "a03. a02s" não muda (começa com letra), e
 * "3.000mah" também não (o número vem colado em letra).
 */
const inheritBrand = (text) => {
    let lead = null;
    return text
        .split('.')
        .map((part) => {
        const tokens = tokenize(part);
        const startsWithNumber = tokens[0] !== undefined &&
            /^\d{1,3}$/.test(tokens[0]) &&
            /^\s*\d{1,3}(\s|$)/.test(part);
        const own = tokens.findIndex((token) => /^\d+$/.test(token));
        const out = startsWithNumber && lead !== null ? ` ${lead} ${part}` : part;
        if (own > 0) {
            const before = tokens[own - 1];
            if (before !== undefined && /^[a-z]+$/.test(before))
                lead = before;
        }
        return out;
    })
        .join('.');
};
/** As palavras que o cadastro de uma seção usa: o vocabulário dela (F244). */
export const sectionVocabulary = (names, qualityLabels) => {
    const vocabulary = new Set();
    for (const name of names) {
        for (const token of nameTokens(name, qualityLabels))
            vocabulary.add(token);
    }
    return vocabulary;
};
/**
 * Os tokens de uma PERGUNTA (F244): os do nome, com o apelido trocado e o
 * modelo que o cliente separou juntado de volta — "a 14" → a14, "4 g" → 4g,
 * "9 t" → 9t. "a", "e" e "o" depois de número são artigo, não letra.
 */
export const queryTokens = (text, qualityLabels) => {
    const out = [];
    for (const raw of nameTokens(text, qualityLabels)) {
        const token = ALIASES.get(raw) ?? raw;
        const previous = out[out.length - 1];
        if (previous !== undefined) {
            const letterThenNumber = JOIN_LETTERS.has(previous) && /^\d{1,4}$/.test(token);
            const numberThenLetter = /^\d{1,3}$/.test(previous) && /^[b-df-np-z]$/.test(token);
            if (letterThenNumber || numberThenLetter) {
                out[out.length - 1] = previous + token;
                continue;
            }
        }
        out.push(token);
    }
    return out;
};
/** Uma letra trocada, a mais ou a menos, ou duas vizinhas invertidas. */
const oneEditApart = (left, right) => {
    if (left === right)
        return true;
    if (Math.abs(left.length - right.length) > 1)
        return false;
    if (left.length === right.length) {
        const diff = [];
        for (let index = 0; index < left.length; index += 1) {
            if (left.charAt(index) !== right.charAt(index))
                diff.push(index);
        }
        const [first, second] = diff;
        if (diff.length === 1)
            return true;
        return (diff.length === 2 &&
            first !== undefined &&
            second === first + 1 &&
            left.charAt(first) === right.charAt(second) &&
            left.charAt(second) === right.charAt(first));
    }
    const [shorter, longer] = left.length < right.length ? [left, right] : [right, left];
    for (let index = 0; index < longer.length; index += 1) {
        if (longer.slice(0, index) + longer.slice(index + 1) === shorter) {
            return true;
        }
    }
    return false;
};
/** Tem dígito e diz o modelo: "a14", "g30", "15c" — não "13" nem "4g". */
const isCode = (token) => /\d/.test(token) && !/^\d+$/.test(token) && !NETWORK_TOKENS.has(token);
/** O modelo contíguo, seguido de algo que não é intruso, em alguma posição. */
export const matchesModel = (tokens, model, intruders) => {
    if (model.length === 0)
        return false;
    for (let start = 0; start + model.length <= tokens.length; start += 1) {
        let contiguous = true;
        for (let offset = 0; offset < model.length; offset += 1) {
            if (tokens[start + offset] !== model[offset]) {
                contiguous = false;
                break;
            }
        }
        if (!contiguous)
            continue;
        const next = tokens[start + model.length];
        if (next === undefined || !intruders.has(next))
            return true;
    }
    return false;
};
const contextFor = (sections, rules, vocabulary) => {
    const triggerOf = new Map();
    for (const section of sections) {
        for (const trigger of section.triggers) {
            for (const token of tokenize(trigger))
                triggerOf.set(token, section);
        }
    }
    // "forntal", "bataria": uma letra fora, só em gatilho de 6+ letras com a
    // mesma inicial — "matéria" nunca vira bateria.
    const long = [...triggerOf.entries()].filter(([word]) => word.length >= 6);
    const sectionOf = (token) => {
        const exact = triggerOf.get(token);
        if (exact !== undefined || token.length < 6)
            return exact;
        return long.find(([word]) => token.startsWith(word.charAt(0)) && oneEditApart(word, token))?.[1];
    };
    const toTokens = (words) => new Set(words.flatMap((word) => tokenize(word)));
    return {
        sections,
        rules,
        sectionOf,
        stop: new Set([...toTokens(rules.stopWords), ...CONNECTORS]),
        otherParts: new Set(OTHER_PART_WORDS.filter((word) => !triggerOf.has(word))),
        intruders: toTokens(rules.intruders),
        brands: toTokens(rules.brandTokens),
        vocabulary,
    };
};
/** Uma peça pedida: as seções, o modelo e as tentativas. */
const parsePart = (piece, context, inherited) => {
    const tokens = queryTokens(piece, context.rules.qualityLabels);
    const found = [];
    const model = [];
    const ignored = [];
    for (let index = 0; index < tokens.length; index += 1) {
        const token = tokens[index];
        if (token === undefined)
            break;
        const section = context.sectionOf(token);
        if (section !== undefined) {
            let before = index - 1;
            while (before >= 0 && CONNECTORS.has(tokens[before] ?? ''))
                before -= 1;
            const previous = before >= 0 ? tokens[before] : undefined;
            // "tampa de bateria": a outra peça manda, e a bateria é parte do nome.
            if (previous !== undefined && context.otherParts.has(previous)) {
                model.push(token);
                continue;
            }
            // "câmera frontal", "vidro da tela": complemento da seção de antes.
            const previousSection = previous === undefined ? undefined : context.sectionOf(previous);
            if (previousSection !== undefined && previousSection !== section) {
                continue;
            }
            if (!found.includes(section))
                found.push(section);
            continue;
        }
        if (context.stop.has(token))
            continue;
        const next = tokens[index + 1];
        const quantity = /^0?\d$/.test(token) &&
            next !== undefined &&
            context.sectionOf(next) !== undefined;
        if (quantity || NUMBER_WORDS.has(token))
            continue;
        if (context.otherParts.has(token)) {
            let end = index;
            while (end < tokens.length && context.otherParts.has(tokens[end] ?? '')) {
                end += 1;
            }
            if (tokens[index - 1] === 'e' || tokens[end] === 'e') {
                ignored.push(...tokens.slice(index, end));
                index = end - 1;
                continue;
            }
        }
        model.push(token);
    }
    const sections = found.length > 0 ? found : inherited;
    if (sections.length === 0)
        return { kind: 'none' };
    // O prefixo da seção já está no nome: "tampa TRASEIRA", "placa de CARGA".
    const prefixWords = new Set(sections.flatMap((section) => section.namePrefix === null ? [] : tokenize(section.namePrefix)));
    let kept = model.filter((token) => {
        if (prefixWords.has(token))
            return false;
        if (QUALIFIERS.has(token)) {
            ignored.push(token);
            return false;
        }
        return true;
    });
    if (kept.length === 0)
        return { kind: 'vague', sections };
    // A palavra que o cadastro dessas seções não usa sai — "amg", "valores",
    // "sams" —, menos a que vem antes de número ("Mate 20" fica, e o robô fica
    // quieto) e a outra peça.
    const vocabularies = sections.map((section) => context.vocabulary?.get(section.key));
    if (vocabularies.length > 0 && vocabularies.every((v) => v !== undefined)) {
        const known = (token) => vocabularies.some((vocabulary) => vocabulary.has(token));
        const filtered = kept.filter((token, index) => {
            const next = kept[index + 1];
            return (/\d/.test(token) ||
                token.length < 2 ||
                known(token) ||
                context.otherParts.has(token) ||
                (next !== undefined && /^\d+$/.test(next)));
        });
        const enough = filtered.length >= 2 || filtered.some((token) => /\d/.test(token));
        if (filtered.length > 0 && filtered.length < kept.length && enough) {
            ignored.push(...kept.filter((token) => !filtered.includes(token)));
            kept = filtered;
        }
    }
    const hasCode = kept.some(isCode);
    const onlyLine = kept.every((token) => GENERIC_WORDS.has(token) ||
        context.intruders.has(token) ||
        NETWORK_TOKENS.has(token));
    const onlyNumbers = kept.every((token) => /^\d+$/.test(token));
    if (!hasCode && (onlyLine || onlyNumbers))
        return { kind: 'vague', sections };
    const attempts = [
        { tokens: kept, ignoreSeriesInName: false, droppedSeries: [] },
    ];
    const relaxed = kept.filter((token) => !context.brands.has(token));
    let loosest = kept;
    if (relaxed.length > 0 && relaxed.length < kept.length) {
        attempts.push({
            tokens: relaxed,
            ignoreSeriesInName: false,
            droppedSeries: [],
        });
        loosest = relaxed;
    }
    const withoutRedmi = loosest.filter((token) => token !== 'redmi');
    if (withoutRedmi.length >= 2 && withoutRedmi.length < loosest.length) {
        attempts.push({
            tokens: withoutRedmi,
            ignoreSeriesInName: false,
            droppedSeries: ['redmi'],
        });
        loosest = withoutRedmi;
    }
    // A última tira "moto"/"redmi" da pergunta e do nome. Sem "redmi", basta um
    // código; com ele, duas palavras ("redmi a2" não vira o Mi A2).
    const seriesFree = loosest.filter((token) => !SERIES.has(token));
    const enough = seriesFree.length >= 2 ||
        (!loosest.includes('redmi') &&
            seriesFree.some((token) => isCode(token) && /[a-z]/.test(token)));
    if (seriesFree.length > 0 && enough) {
        attempts.push({
            tokens: seriesFree,
            ignoreSeriesInName: true,
            droppedSeries: [
                ...new Set(kept.filter((token) => SERIES.has(token) && !seriesFree.includes(token))),
            ],
        });
    }
    // Na ordem em que a loja as cadastrou, não na ordem da frase.
    const ordered = context.sections.filter((section) => sections.includes(section));
    return {
        kind: 'query',
        sections,
        query: {
            sections: ordered,
            attempts,
            normalized: tokenize(piece).join(' '),
            ignored,
            needsCode: !hasCode,
        },
    };
};
/**
 * A mensagem do cliente vira as peças pedidas (F244) — ou silêncio:
 *
 * - `no_trigger`: nenhuma palavra de seção — não é pergunta para o robô;
 * - `silent`: cita peça, mas o robô não deve responder: a mensagem já tem
 *   preço, é pós-venda, ou é vaga ("tela do iPhone", "bateria de 13");
 * - `query`: uma parte por linha (ou por "/"), até 5. A linha sem palavra de
 *   seção herda a da anterior: "Tela Moto E6s / Samsung A32".
 *
 * `vocabulary` (por `section.key`) liga a regra das palavras que o cadastro
 * não usa. Sem ele, elas ficam no modelo, como antes do F244.
 */
export const parseCatalogMessage = (message, sections, rules, vocabulary) => {
    const normalized = tokenize(message).join(' ');
    const context = contextFor(sections, rules, vocabulary);
    const lines = message.split(/\r?\n|\//).filter((line) => line.trim() !== '');
    const pieces = lines.length >= 2 ? lines : [message];
    let inherited = [];
    let triggered = false;
    const parts = [];
    for (const piece of pieces) {
        const parsed = parsePart(piece, context, inherited);
        if (parsed.kind === 'none')
            continue;
        triggered = true;
        inherited = parsed.sections;
        if (parsed.kind === 'query' && parts.length < MAX_PARTS) {
            parts.push(parsed.query);
        }
    }
    if (!triggered)
        return { kind: 'no_trigger', normalized };
    if (PRICE_PATTERN.test(normalizeText(message))) {
        return { kind: 'silent', reason: 'price', normalized };
    }
    const words = queryTokens(message, rules.qualityLabels);
    if (words.some((word) => AFTER_SALE.has(word))) {
        return { kind: 'silent', reason: 'after_sale', normalized };
    }
    if (parts.length === 0)
        return { kind: 'silent', reason: 'vague', normalized };
    return { kind: 'query', parts, normalized };
};
/**
 * A primeira peça pedida, ou `null` quando o robô não responderia — o jeito
 * de antes do F244, que os testes-ouro usam.
 */
export const parseCatalogQuery = (message, sections, rules, vocabulary) => {
    const parsed = parseCatalogMessage(message, sections, rules, vocabulary);
    return parsed.kind === 'query' ? (parsed.parts[0] ?? null) : null;
};
/** A peça pertence à seção pelo prefixo do nome (`f.`, `bat.`). */
export const belongsToSection = (name, section) => {
    if (section.namePrefix === null)
        return true;
    const prefix = normalizeText(section.namePrefix).trim();
    return prefix !== '' && normalizeText(name).trimStart().startsWith(prefix);
};
/** O nome como o cliente lê: sem o prefixo da seção, espaços colapsados. */
export const displayName = (name, section) => {
    let shown = name.trim();
    if (section.namePrefix !== null && belongsToSection(shown, section)) {
        shown = shown.slice(section.namePrefix.trim().length);
    }
    return shown.replace(/\s+/g, ' ').trim();
};
/**
 * As peças que respondem à pergunta, numa seção, e o motivo de cada recusa
 * (a caixa de teste mostra). A primeira tentativa que acha algo vence — a
 * marca só é relaxada quando a pergunta com ela não acha nada.
 *
 * Com `maxItems`, a pergunta sem código que traz mais peças do que isso é
 * vaga (F244): "flex do sensor" responde, "Flex Power" (168) não.
 */
export const selectForSection = (candidates, query, section, rules, excludedProductIds = new Set()) => {
    const intruders = new Set(rules.intruders.flatMap((word) => tokenize(word)));
    const inSection = candidates.filter((candidate) => belongsToSection(candidate.name, section));
    const rejected = candidates
        .filter((candidate) => !inSection.includes(candidate))
        .map((candidate) => ({ candidate, reason: 'other_section' }));
    const tokensOf = new Map(inSection.map((candidate) => [
        candidate,
        nameTokens(candidate.name, rules.qualityLabels),
    ]));
    let matched = [];
    let lastMisses = inSection;
    let vague = false;
    for (const attempt of query.attempts) {
        const family = new Set(attempt.droppedSeries.flatMap((series) => BRAND_FAMILIES[series] ?? []));
        const hits = inSection.filter((candidate) => {
            const tokens = tokensOf.get(candidate) ?? [];
            const otherBrand = family.size > 0 &&
                tokens.some((token) => BRAND_WORDS.has(token) && !family.has(token));
            if (otherBrand)
                return false;
            return matchesModel(attempt.ignoreSeriesInName
                ? tokens.filter((token) => !SERIES.has(token))
                : tokens, attempt.tokens, intruders);
        });
        if (hits.length > 0) {
            if (query.needsCode &&
                rules.maxItems !== undefined &&
                hits.length > rules.maxItems) {
                vague = true;
                break;
            }
            matched = hits;
            lastMisses = inSection.filter((candidate) => !hits.includes(candidate));
            break;
        }
    }
    for (const candidate of lastMisses) {
        rejected.push({ candidate, reason: 'model' });
    }
    const kept = matched.filter((candidate) => !excludedProductIds.has(candidate.productId));
    for (const candidate of matched) {
        if (excludedProductIds.has(candidate.productId)) {
            rejected.push({ candidate, reason: 'excluded' });
        }
    }
    return { matched: kept, rejected, vague };
};
/**
 * A resposta em Markdown — a outbox guarda Markdown, e `markdownToWhatsApp`
 * (F192) transforma `**x**` no `*x*` do WhatsApp. `null` quando nada sobra:
 * o robô fica quieto.
 *
 * ```
 * **📱 TELAS**
 *
 * A03 COM ARO (NN) **R$ 80,00**
 *
 * A03 COM ARO (WK) **R$ 95,00**
 * ```
 */
export const formatPriceReply = (groups, maxItems) => {
    const blocks = [];
    for (const group of groups) {
        const seen = new Set();
        const lines = [];
        for (const line of group.lines) {
            const name = displayName(line.name, group.section);
            const code = line.code?.trim() ?? '';
            const text = `${code === '' ? '' : `${code} `}${name} **${line.priceLabel}**`;
            if (seen.has(text))
                continue;
            seen.add(text);
            lines.push(text);
            if (lines.length >= maxItems)
                break;
        }
        if (lines.length === 0)
            continue;
        const header = `**${group.section.emoji} ${group.section.title.toUpperCase()}**`;
        blocks.push([header, ...lines].join('\n\n'));
    }
    return blocks.length === 0 ? null : blocks.join('\n\n');
};
/**
 * Parece resposta de robô: preço E emoji. Dois robôs de tabela se
 * respondendo — o nosso e o de um fornecedor — trocariam mensagens até o
 * teto por hora. O critério é o do robô antigo, que já passou por isso.
 */
export const looksLikeBotReply = (text) => /r\$/i.test(text) && /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(text);
//# sourceMappingURL=price-reply.js.map